import topbarCss from '../css/topbar.css';
import { fetchJson, fetchText } from '../utils/fetch';
import { initBehaviors } from '../behaviors/init';
import { resolveDefaultTopbarSrc } from '../utils/loader';
const MATERIAL_LINK = 'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,400,0,0';
const DEFAULT_USERINFO_URL = '/auth/userinfo';
const DEFAULT_AVATAR_TEXT = 'UN';

type UserInfoResponse = {
  account?: { name?: string; srn?: string };
  login_session?: { idp?: string; srn?: string; role?: { name?: string; srn?: string } };
  user?: { email?: string; name?: string; picture?: string; roles?: string[]; srn?: string };
};

type UserInfo = {
  accountName?: string;
  accountId?: string;
  role?: string;
  userName?: string;
  email?: string;
  picture?: string;
};

const SHELL_HTML = `
  <header class="topbar" role="banner">
    <div class="left">
      <a class="logo" href="#" aria-label="Home">
        <span class="logo-mark">UN</span>
      </a>
      <nav class="menu" aria-label="Primary">
        <div class="dropdown">
          <button class="nav-item dropdown-toggle" type="button" aria-haspopup="true" aria-expanded="false">
            <span>Applications</span>
            <span class="material-symbols-outlined" aria-hidden="true" style="margin-left:4px;">expand_more</span>
          </button>
          <div class="dropdown-menu menu-apps" data-menu="apps" role="menu">
            <div class="menu-header"><span class="title">Loading…</span></div>
          </div>
        </div>
      </nav>
    </div>
    <div class="center">
      <form class="search" role="search" aria-label="Global search">
        <span class="icon" aria-hidden="true"><span class="material-symbols-outlined">search</span></span>
        <input type="search" placeholder="Search services, features, and more" aria-label="Search" />
      </form>
    </div>
    <div class="right">
      <div class="dropdown">
        <button class="icon-btn dropdown-toggle badge-notifications" type="button" title="Notifications" aria-label="Notifications" aria-haspopup="true" aria-expanded="false">
          <span class="material-symbols-outlined" aria-hidden="true">notifications</span>
        </button>
        <div class="dropdown-menu dropdown-right menu-notifications" data-menu="notifications" role="menu">
          <div class="menu-header"><span class="title">Loading…</span></div>
        </div>
      </div>
      <div class="dropdown">
        <button class="icon-btn dropdown-toggle badge-messages" type="button" title="Messages" aria-label="Messages" aria-haspopup="true" aria-expanded="false">
          <span class="material-symbols-outlined" aria-hidden="true">chat_bubble</span>
        </button>
        <div class="dropdown-menu dropdown-right menu-messages" data-menu="messages" role="menu">
          <div class="menu-header"><span class="title">Loading…</span></div>
        </div>
      </div>
      <div class="dropdown">
        <button class="avatar dropdown-toggle" type="button" title="Account menu" aria-label="Account menu" aria-haspopup="true" aria-expanded="false">WA</button>
        <div class="dropdown-menu dropdown-right" data-menu="account" role="menu">
          <div class="menu-header"><span class="title">Loading…</span></div>
        </div>
      </div>
    </div>
  </header>
`;

export class S2STopbar extends HTMLElement {
  // Renders the visible shell immediately, then hydrates dropdown content from a remote HTML response.
  static get observedAttributes() { return ["src","dropdown-src","ribbon-color","account-name","account-id","user-role","userinfo-src"] as const; }
  private root: ShadowRoot;
  private static cache: Map<string,string> = new Map();
  private static userinfoCache: Map<string,UserInfo> = new Map();
  private slotEl!: HTMLDivElement;
  private loadingEl!: HTMLDivElement;
  private errorEl!: HTMLDivElement;
  private cleanup: (() => void) | null = null;
  private ribbonEl!: HTMLDivElement;
  private ribbonAccountEl!: HTMLSpanElement;
  private ribbonRoleEl!: HTMLSpanElement;
  private userInfo: UserInfo | null = null;
  private userinfoReq = 0;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
    // Kick off icon font load immediately to avoid icon text flashing before styles apply
    ensureMaterialFont();
    const style = document.createElement('style'); style.textContent = topbarCss;
    // Ribbon container
    this.ribbonEl = document.createElement('div');
    this.ribbonEl.className = 's2s-ribbon';
    // Content wrapper that shows only when data is present
    const ribbonContent = document.createElement('div');
    ribbonContent.className = 's2s-ribbon-content';
    // Single data pill containing both parts
    const badgeWrap = document.createElement('div');
    badgeWrap.className = 'badge data';
    this.ribbonAccountEl = document.createElement('span');
    this.ribbonAccountEl.className = 'account';
    this.ribbonRoleEl = document.createElement('span');
    this.ribbonRoleEl.className = 'role';
    badgeWrap.append(this.ribbonAccountEl, this.ribbonRoleEl);
    ribbonContent.append(badgeWrap);
    this.ribbonEl.append(ribbonContent);
    this.loadingEl = document.createElement('div'); this.loadingEl.textContent = 'Loading topbar…'; this.loadingEl.style.cssText = 'height:56px;display:grid;place-items:center;color:#fff;background:var(--topbar-bg,#17223a);';
    this.errorEl = document.createElement('div'); this.errorEl.textContent = 'Failed to load topbar'; this.errorEl.style.cssText = 'display:none;height:56px;display:grid;place-items:center;background:#8b1d1d;color:#fff;';
    this.slotEl = document.createElement('div'); this.slotEl.className = 's2s-slot'; this.slotEl.style.display = 'none';
    this.root.append(style, this.ribbonEl, this.loadingEl, this.errorEl, this.slotEl);
  }

  connectedCallback() {
    const dropdownSrc = this.getAttribute('dropdown-src') || this.getAttribute('src');
    if (dropdownSrc) this.load(dropdownSrc);
    else {
      const fallback = resolveDefaultTopbarSrc();
      if (fallback) this.setAttribute('src', fallback);
    }
    this.#applyRibbonAttrs();
    this.#loadUserInfo();
  }
  attributeChangedCallback(name: string, _o: string|null, v: string|null) {
    if (!this.isConnected) return;
    if ((name==='src' || name==='dropdown-src') && v) this.load(v);
    if (name==='ribbon-color' || name==='account-name' || name==='account-id' || name==='user-role') this.#applyRibbonAttrs();
    if (name==='userinfo-src') this.#loadUserInfo();
  }

  async load(url: string) {
    this.#reset();
    this.#renderShell();
    this.#applyUserInfo();
    this.loadingEl.style.display = 'none'; this.slotEl.style.display = 'block';
    this.#ensureBodyOffset();
    this.cleanup = initBehaviors(this.root);
    this.#updateRibbonOffset();
    try {
      await this.#hydrateDropdowns(url);
    } catch {
      this.#showDropdownError();
    }
  }

  #reset() { this.loadingEl.style.display = 'grid'; this.errorEl.style.display = 'none'; this.slotEl.style.display = 'none'; this.slotEl.innerHTML = ''; if (this.cleanup) { try { this.cleanup(); } catch {} this.cleanup = null; } }

  #renderShell() { this.slotEl.innerHTML = SHELL_HTML; }

  async #hydrateDropdowns(url: string) {
    let html: string;
    if (S2STopbar.cache.has(url)) {
      html = S2STopbar.cache.get(url)!;
    } else {
      html = await fetchText(url);
      S2STopbar.cache.set(url, html);
    }
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const menuContent: Record<string, Element | null> = {
      apps: doc.querySelector('.dropdown-menu.menu-apps'),
      notifications: doc.querySelector('.dropdown-menu.menu-notifications'),
      messages: doc.querySelector('.dropdown-menu.menu-messages'),
      account: this.#findAccountMenu(doc)
    };
    Object.entries(menuContent).forEach(([key, node]) => this.#applyMenuContent(key, node?.innerHTML || null));
    this.#rebindBehaviors();
    this.#updateRibbonOffset();
  }

  #applyMenuContent(key: string, html: string | null) {
    const target = this.root.querySelector(`.dropdown-menu[data-menu="${key}"]`) as HTMLElement | null;
    if (!target) return;
    if (html) target.innerHTML = html;
    else target.innerHTML = '<div class="menu-header"><span class="title">Unavailable</span></div>';
  }

  #findAccountMenu(doc: Document): Element | null {
    const avatar = doc.querySelector('.avatar.dropdown-toggle');
    const fromAvatar = avatar?.closest('.dropdown')?.querySelector('.dropdown-menu');
    if (fromAvatar) return fromAvatar;
    return doc.querySelector('.dropdown-menu:not(.menu-apps):not(.menu-notifications):not(.menu-messages)');
  }

  #rebindBehaviors() {
    if (this.cleanup) { try { this.cleanup(); } catch {} }
    this.cleanup = initBehaviors(this.root);
  }

  #showDropdownError() {
    ['apps','notifications','messages','account'].forEach((key) => this.#applyMenuContent(key, '<div class="menu-header"><span class="title">Failed to load</span></div>'));
  }

  #ensureBodyOffset() {
    if (this.hasAttribute('no-offset')) return;
    const header = this.root.querySelector('header.topbar') as HTMLElement | null; if (!header) return;
    const h = header.getBoundingClientRect().height || 56;
    const ribbonH = this.ribbonEl && this.ribbonEl.style.display !== 'none' ? this.ribbonEl.getBoundingClientRect().height : 0;
    document.documentElement.style.setProperty('--s2s-topbar-height', `${h + ribbonH}px`);
    const current = getComputedStyle(document.body).marginTop; if (!current || current === '0px') document.body.style.marginTop = 'var(--s2s-topbar-height)';
  }

  #applyRibbonAttrs() {
    const color = this.getAttribute('ribbon-color');
    const name = this.getAttribute('account-name') || this.userInfo?.accountName || null;
    const id = this.getAttribute('account-id') || this.userInfo?.accountId || null;
    const role = this.getAttribute('user-role') || this.userInfo?.role || null;
    const hasData = !!(name || id || role);
    const content = this.ribbonEl.querySelector('.s2s-ribbon-content') as HTMLElement | null;
    if (content) content.style.display = hasData ? 'inline-flex' : 'none';
    if (color) (this as HTMLElement).style.setProperty('--s2s-ribbon-bg', color);
    const accountText = [name ? String(name) : null, id ? `(${id})` : null].filter(Boolean).join(' ');
    this.ribbonAccountEl.textContent = accountText ? `Account: ${accountText}` : '';
    this.ribbonRoleEl.textContent = role ? `Role: ${role}` : '';
    this.#updateRibbonOffset();
  }

  #updateRibbonOffset() {
    const base = parseFloat(getComputedStyle(this).getPropertyValue('--s2s-ribbon-base')) || 10;
    // Keep the topbar offset around 10px regardless of content height
    (this as HTMLElement).style.setProperty('--s2s-ribbon-height', `${base}px`);
    this.#ensureBodyOffset();
  }

  async #loadUserInfo() {
    const raw = this.getAttribute('userinfo-src');
    if (raw === '') return;
    const url = raw || DEFAULT_USERINFO_URL;
    const requestId = ++this.userinfoReq;
    try {
      const info = await this.#fetchUserInfo(url);
      if (requestId !== this.userinfoReq) return;
      this.userInfo = info;
      this.#applyUserInfo();
      this.#applyUserInfoAttrs();
    } catch {
      // silently ignore userinfo failures
    }
  }

  async #fetchUserInfo(url: string): Promise<UserInfo> {
    if (S2STopbar.userinfoCache.has(url)) return S2STopbar.userinfoCache.get(url)!;
    const data = await fetchJson<UserInfoResponse>(url, {
      credentials: 'include',
      headers: { 'Accept': 'application/json' }
    });
    const role = data.login_session?.role?.name
      || data.login_session?.role?.srn
      || data.user?.roles?.[0];
    const info: UserInfo = {
      accountName: data.account?.name,
      accountId: data.account?.srn,
      role,
      userName: data.user?.name,
      email: data.user?.email,
      picture: data.user?.picture
    };
    S2STopbar.userinfoCache.set(url, info);
    return info;
  }

  #applyUserInfoAttrs() {
    if (!this.userInfo) return;
    const { accountName, accountId, role } = this.userInfo;
    if (!this.getAttribute('account-name') && accountName) this.setAttribute('account-name', accountName);
    if (!this.getAttribute('account-id') && accountId) this.setAttribute('account-id', accountId);
    if (!this.getAttribute('user-role') && role) this.setAttribute('user-role', role);
  }

  #applyUserInfo() {
    if (!this.userInfo) return;
    const avatar = this.root.querySelector('button.avatar') as HTMLButtonElement | null;
    if (!avatar) return;
    const name = this.userInfo.userName || this.userInfo.email;
    const initials = name ? getInitials(name) : DEFAULT_AVATAR_TEXT;
    if (name) {
      avatar.title = name;
      avatar.setAttribute('aria-label', name);
    }
    if (this.userInfo.picture) {
      avatar.classList.add('has-image');
      avatar.style.backgroundImage = `url("${this.userInfo.picture}")`;
      avatar.textContent = initials;
    } else {
      avatar.classList.remove('has-image');
      avatar.style.backgroundImage = '';
      if (name) avatar.textContent = initials;
    }
  }
}

function ensureMaterialFont() {
  const hasMaterialSheet = () => Array.from(document.querySelectorAll('link[rel="stylesheet"]')).some(l => (l as HTMLLinkElement).href.includes('Material+Symbols+Outlined'));
  if (hasMaterialSheet()) return;
  const head = document.head || document.documentElement;
  const addPreconnect = (href: string, crossOrigin = false) => {
    if (Array.from(document.querySelectorAll(`link[rel="preconnect"][href="${href}"]`)).length) return;
    const l = document.createElement('link');
    l.rel = 'preconnect';
    l.href = href;
    if (crossOrigin) l.crossOrigin = '';
    head.appendChild(l);
  };
  addPreconnect('https://fonts.googleapis.com');
  addPreconnect('https://fonts.gstatic.com', true);
  const preload = document.createElement('link');
  preload.rel = 'preload';
  preload.as = 'style';
  preload.href = MATERIAL_LINK;
  preload.onload = function() { (this as HTMLLinkElement).rel = 'stylesheet'; };
  head.appendChild(preload);
  // Fallback in case preload rel swap does not fire
  setTimeout(() => {
    if (!hasMaterialSheet()) {
      const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = MATERIAL_LINK; head.appendChild(l);
    }
  }, 0);
}

function getInitials(value: string): string {
  const cleaned = value.trim();
  if (!cleaned) return DEFAULT_AVATAR_TEXT;
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}
